import React from 'react';
import { ExternalLink } from 'lucide-react';
import { useCmsList } from '../../hooks/useCms';
import { FALLBACK_PARTNERS, FALLBACK_SPONSOR } from '../../data/cmsFallbacks';
import { useLanguage } from '../../context/LanguageContext';
import { translations } from '../../data/translations';

const SponsorSection = () => {
  const { t } = useLanguage();
  const allPartners = useCmsList('/api/cms/partners', FALLBACK_PARTNERS);
  const flagged = allPartners.filter((p) => p.is_sponsor);
  const sponsors = flagged.length ? flagged : [FALLBACK_SPONSOR];

  return (
    <section className="relative py-32 bg-white overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-black/10 to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="h-px w-12 bg-[#D4AF37]" />
            <span className="text-black/50 text-sm font-medium uppercase tracking-[0.3em]">
              {t('partnership', { de: 'Partnerschaft', en: 'Partnership' })}
            </span>
            <div className="h-px w-12 bg-[#D4AF37]" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#003366]">
            {t('sponsor', translations.partners)}
          </h2>
        </div>

        {/* Sponsor Card */}
        <div className={`mx-auto grid gap-8 ${sponsors.length > 1 ? 'max-w-6xl md:grid-cols-2' : 'max-w-4xl'}`}>
          {sponsors.map((sponsor) => (
          <div key={sponsor.id} className="group relative p-12 rounded-3xl bg-[#F9F9F9] border border-black/5 hover:border-[#D4AF37]/30 transition-all duration-500">
            {/* Top-left corner frame with animated golden glow - extends to middle */}
            <div className="absolute top-0 left-0 w-1/2 h-1/2 overflow-hidden rounded-tl-3xl pointer-events-none">
              {/* Vertical black line */}
              <div className="absolute top-0 left-0 w-[2px] h-full bg-black">
                <div 
                  className="absolute left-0 w-full h-[60px]"
                  style={{
                    background: 'linear-gradient(180deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
                    animation: 'cornerGlowVertical 4s ease-in-out infinite',
                    boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
                  }}
                />
              </div>
              {/* Horizontal black line */}
              <div className="absolute top-0 left-0 h-[2px] w-full bg-black">
                <div 
                  className="absolute top-0 h-full w-[60px]"
                  style={{
                    background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
                    animation: 'cornerGlowHorizontal 4s ease-in-out infinite',
                    boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
                  }}
                />
              </div>
            </div>
            
            {/* Bottom-right corner frame with animated golden glow - extends to middle */}
            <div className="absolute bottom-0 right-0 w-1/2 h-1/2 overflow-hidden rounded-br-3xl pointer-events-none">
              {/* Vertical black line */}
              <div className="absolute bottom-0 right-0 w-[2px] h-full bg-black">
                <div 
                  className="absolute right-0 w-full h-[60px]"
                  style={{
                    background: 'linear-gradient(0deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
                    animation: 'cornerGlowVerticalReverse 4s ease-in-out infinite',
                    boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
                  }}
                />
              </div>
              {/* Horizontal black line */}
              <div className="absolute bottom-0 right-0 h-[2px] w-full bg-black">
                <div 
                  className="absolute bottom-0 h-full w-[60px]"
                  style={{
                    background: 'linear-gradient(270deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
                    animation: 'cornerGlowHorizontalReverse 4s ease-in-out infinite',
                    boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
                  }}
                />
              </div>
            </div>

            <div className="flex flex-col lg:flex-row items-center gap-12">
              {/* Logo - Original colors */}
              <div className="flex-shrink-0">
                <div className="relative w-48 h-48 rounded-2xl bg-white p-6 flex items-center justify-center shadow-lg shadow-black/5">
                  <img src={sponsor.logo_url} alt={sponsor.name} className="w-full h-full object-contain" />
                </div>
              </div>

              <div className="flex-1 text-center lg:text-left">
                <h3 className="text-3xl font-bold text-[#003366] mb-4">{sponsor.name}</h3>
                <p className="text-black/60 text-lg leading-relaxed mb-6">
                  {t('d', { de: sponsor.description_de, en: sponsor.description_en }) || t('sponsorDesc', translations.partners)}
                </p>
                <a href={sponsor.website_url || FALLBACK_SPONSOR.website_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-[#003366] hover:bg-[#C9A227] rounded-md border border-[#B8960C] transition-all duration-300 font-medium">
                  <span>{t('visitWebsite', { de: 'Website besuchen', en: 'Visit Website' })}</span>
                  <ExternalLink size={18} />
                </a>
              </div>
            </div>
          </div>
          ))}
        </div>
      </div>

      {/* Corner Glow Animations */}
      <style>{`
        @keyframes cornerGlowVertical {
          0% { top: -40px; opacity: 0; }
          15% { opacity: 1; }
          85% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes cornerGlowHorizontal {
          0% { left: -40px; opacity: 0; }
          15% { opacity: 1; }
          85% { opacity: 1; }
          100% { left: 100%; opacity: 0; }
        }
        @keyframes cornerGlowVerticalReverse {
          0% { bottom: -40px; opacity: 0; }
          15% { opacity: 1; }
          85% { opacity: 1; }
          100% { bottom: 100%; opacity: 0; }
        }
        @keyframes cornerGlowHorizontalReverse {
          0% { right: -40px; opacity: 0; }
          15% { opacity: 1; }
          85% { opacity: 1; }
          100% { right: 100%; opacity: 0; }
        }
      `}</style>
    </section>
  );
};

export default SponsorSection;
